import { all, takeLatest } from 'redux-saga/effects';
import { inviteUsersSaga, cancelInvitesSaga, resendInvitesSaga } from './invite-sagas';
import { sendAddNewSharedComputer, sendModifySharedComputer } from './shared-computer-sagas';
import { adminSyncForceUpdate } from './force-update';

import * as Types from '../types';

// Minimal shape of the admin client config threaded through every watcher into
// the worker sagas that read `baseApiUrl` / `clientStorePath`.
interface AdminClientConfig {
  baseApiUrl?: string;
  clientStorePath?: string;
  getAuthToken?: () => string | null | undefined;
}

// Uniform watcher-boundary signature for the worker sagas. Each worker module
// is typed independently (stricter config shapes, optional trailing `timeout`
// defaults), which `takeLatest` cannot reconcile; casting to this shared shape
// erases those differences without changing runtime behavior (no `timeout` arg
// is forwarded, so each worker's default applies).
type AdminClientWorker = (
  config: AdminClientConfig,
  action: { type: string; payload?: unknown },
) => Generator<unknown, void, unknown>;

/* istanbul ignore next reason: no saga watch tests */
export function* watchInviteUsersSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_PRODUCT_ACCESS_INVITE_USERS,
    inviteUsersSaga as unknown as AdminClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchCancelInvitesSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_PRODUCT_ACCESS_CANCEL_INVITES,
    cancelInvitesSaga as unknown as AdminClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchResendInvitesSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_PRODUCT_ACCESS_RESEND_INVITES,
    resendInvitesSaga as unknown as AdminClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSendAddNewSharedComputerSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_USER_ADMIN_ADD_SHARED_COMPUTER,
    sendAddNewSharedComputer as unknown as AdminClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchSendModifySharedComputerSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_USER_ADMIN_MODIFY_SHARED_COMPUTER,
    sendModifySharedComputer as unknown as AdminClientWorker,
    config,
  );
}

/* istanbul ignore next reason: no saga watch tests */
export function* watchAdminSyncForceUpdateSaga(
  config: AdminClientConfig,
): Generator<unknown, void, unknown> {
  yield takeLatest(
    Types.REQ_ADMIN_SYNC_FORCE_UPDATE,
    adminSyncForceUpdate as unknown as AdminClientWorker,
    config,
  );
}

// export wrapper function to capture client config for sagas to read from
/* istanbul ignore next reason: no rootSaga tests */
export default (config: AdminClientConfig = {}) =>
  // return our rootSaga
  function* rootSaga(): Generator<unknown, void, unknown> {
    yield all([
      // import all additional watcher sagas here
      watchInviteUsersSaga(config),
      watchCancelInvitesSaga(config),
      watchResendInvitesSaga(config),
      watchSendAddNewSharedComputerSaga(config),
      watchSendModifySharedComputerSaga(config),
      watchAdminSyncForceUpdateSaga(config),
    ]);
  };
